import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { CompanyInfo } from '../data/models/company.model';
import { getPurchasedEventTypes } from 'data/api/company.api';

export interface CompanyState {
  value: CompanyInfo;
  status: 'idle' | 'loading' | 'failed';

  purchasedEventTypes: PurchasedEventTypes[] | null;
  statusPurchasedEventTypes: 'idle' | 'loading' | 'success' | 'failed';
}

export interface PurchasedEventTypes {
  eventId: string;
  name: string;
  description?: string;
  icon?: string;
  hasChecklist: boolean;
  hasCareCoordination: boolean;
  hasGrief: boolean;
  hasPartners: boolean;
  hasResources: boolean;
  hasSharing: boolean;
  heading: string;
  abbreviatedName?: string;
  memberCount?: number;
}

export const getPurchasedEventType = createAsyncThunk(
  'company/getPurchasedEventTypesAsync',
  async (companyId: string): Promise<any> => {
    const response = await getPurchasedEventTypes(companyId);
    return response;
  },
);

const initialState: CompanyState = {
  value: {
    companyId: '',
    name: '',
    policyDescription: '',
    logo: '',
    userCount: 0,
    defaultState: '',
    hasEmployees: false,
    hasNonEmployees: false,
  },
  status: 'idle',
  purchasedEventTypes: null,
  statusPurchasedEventTypes: 'idle',
};

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    saveCompany: (state, action: PayloadAction<CompanyInfo>) => {
      state.value = action.payload;
    },
    clearCompany: (state) => {
      state.value = {
        companyId: '',
        name: '',
        policyDescription: '',
        logo: '',
        userCount: 0,
        defaultState: '',
        hasEmployees: false,
        hasNonEmployees: false,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPurchasedEventType.pending, (state) => {
        state.purchasedEventTypes = null;
      })
      .addCase(
        getPurchasedEventType.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.purchasedEventTypes = action.payload.events;
        },
      )
      .addCase(getPurchasedEventType.rejected, (state) => {
        state.purchasedEventTypes = null;
      });
  },
});

export const { saveCompany, clearCompany } = companySlice.actions;
export const selectCompany = (state: RootState) => state.company.value;
export const selectPurchasedEventTypes = (state: RootState) =>
  state.company.purchasedEventTypes;

export default companySlice.reducer;
